import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { makeStyles } from "@material-ui/styles";
import IconButton from "@material-ui/core/IconButton";
import ChevronLeftIcon from "@material-ui/icons/ChevronLeft";
import ChevronRightIcon from "@material-ui/icons/ChevronRight";
import useMediaQuery from "@material-ui/core/useMediaQuery";
import { useTheme } from "@material-ui/core/styles";
import clsx from "clsx";

const useStyles = makeStyles((theme) => ({
    root: {
        position: "relative",
        overflow: "hidden",
        width: "100%",
    },
    container: {
        display: "flex",
    },
    item: {
        flexShrink: 0,
        boxSizing: "border-box",
        padding: theme.spacing(1),
    },
    navButton: {
        '&:hover': {
            color: theme.palette.primary.main,
        }
    },
    navButtonLeft: {
        position: "absolute",
        top: "50%",
        left: 0,
        transform: "translateY(-50%)",
        zIndex: 10,
    },
    navButtonRight: {
        position: "absolute",
        top: "50%",
        right: 0,
        transform: "translateY(-50%)",
        zIndex: 10,
    },
    indicators: {
        display: "flex",
        justifyContent: "center",
        marginTop: theme.spacing(1),
        gap: theme.spacing(1),
        position: 'absolute',
        bottom: 0,
        justifySelf: 'center'
    },
    indicatorDot: {
        width: 10,
        height: 10,
        borderRadius: "50%",
        cursor: "pointer",
        backgroundColor: theme.palette.divider,
        "&.active": {
            backgroundColor: theme.palette.primary.main,
        },
    },
}));

export default function Carousel({
    slidesToShow = { xs: 1, sm: 2, md: 3 },
    slidesToScroll = 1,
    children,
    infinite = true,
    autoplay = false,
    autoplayInterval = 4000,
    showIndicators = true,
}) {

    const classes = useStyles();
    const theme = useTheme();

    const isXs = useMediaQuery(theme.breakpoints.down("xs"));
    const isSm = useMediaQuery(theme.breakpoints.between("sm", "md"));

    let visibleCount = slidesToShow.md || 3;
    if (isXs) visibleCount = slidesToShow.xs || 1;
    else if (isSm) visibleCount = slidesToShow.sm || 2;

    const items = React.Children.toArray(children);
    const total = items.length;

    const [index, setIndex] = useState(0);
    const [paused, setPaused] = useState(false);

    const maxIndex = Math.max(0, total - visibleCount);
    // detectar si hay suficientes slides
    const hideNavigation = total <= visibleCount;
    // autoplay efectivo
    const effectiveAutoplay = autoplay && total > visibleCount;

    const handleNext = () => {

        if (hideNavigation) return;

        if (infinite) {

            if (index >= maxIndex) {
                setIndex(0);
                return;
            }

            setIndex(prev => prev + slidesToScroll);

        } else {

            if (index >= maxIndex) return;
            setIndex(prev => prev + slidesToScroll);

        }

    };

    const handlePrev = () => {
        if (hideNavigation) return;
        if (infinite) {
            if (index <= 0) {
                setIndex(maxIndex);
                return;
            }
            setIndex(prev => prev - slidesToScroll);

        } else {
            if (index <= 0) return;
            setIndex(prev => prev - slidesToScroll);
        }

    };

    const handleIndicatorClick = (i) => {
        setIndex(i);
    };

    useEffect(() => {

        if (!effectiveAutoplay || paused) return;
        const timer = setInterval(() => {
            if (!infinite && index >= maxIndex) return;
            handleNext();
        }, autoplayInterval);

        return () => clearInterval(timer);

    }, [index, effectiveAutoplay, autoplayInterval, paused]);

    const totalIndicators = Math.max(1, total - visibleCount + 1);
    const disablePrev = !infinite && index <= 0;
    const disableNext = !infinite && index >= maxIndex;

    return (
        <div
            className={classes.root}
            onMouseEnter={() => setPaused(true)}
            onMouseLeave={() => setPaused(false)}
        >
            {!hideNavigation && (
                <IconButton
                    className={clsx(classes.navButtonLeft, classes.navButton)}
                    onClick={handlePrev}
                    disabled={disablePrev}
                >
                    <ChevronLeftIcon />
                </IconButton>
            )}

            {!hideNavigation && (
                <IconButton
                    className={clsx(classes.navButtonRight, classes.navButton)}
                    onClick={handleNext}
                    disabled={disableNext}
                >
                    <ChevronRightIcon />
                </IconButton>
            )}

            <div
                className={classes.container}
                style={{
                    transform: `translateX(-${(index * 100) / visibleCount}%)`,
                    transition: "transform 0.5s ease"
                }}
            >
                {items.map((child, i) => (
                    <div
                        key={i}
                        className={classes.item}
                        style={{ width: `${100 / visibleCount}%` }}
                    >
                        {child}
                    </div>
                ))}
            </div>

            {showIndicators && totalIndicators > 1 && !hideNavigation && (

                <div className={classes.indicators}>
                    {Array.from({ length: totalIndicators }).map((_, i) => (
                        <div
                            key={i}
                            onClick={() => handleIndicatorClick(i)}
                            className={clsx(
                                classes.indicatorDot,
                                i === index ? "active" : ""
                            )}
                        />
                    ))}
                </div>
            )}
        </div>
    );
}

Carousel.propTypes = {
    slidesToShow: PropTypes.object,
    slidesToScroll: PropTypes.number,
    children: PropTypes.node,
    infinite: PropTypes.bool,
    autoplay: PropTypes.bool,
    autoplayInterval: PropTypes.number,
    showIndicators: PropTypes.bool,
};